/**
 * AppTopbar.tsx - Topbar do shell autenticado do Órbita.
 *
 * Referência: NAVEGACAO.md §3.2, UX.md §2.3
 * Conteúdo: hamburguer (mobile) | logo tenant | "Órbita by Tenant" | título módulo | avatar
 */

import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router";
import { useAuth, useOrbUser } from "@/features/auth/AuthContext";
import { useTenant } from "./TenantContext";

// ---------------------------------------------------------------------------
// Helper: resolve título do módulo a partir da rota
// ---------------------------------------------------------------------------
function resolveModuleTitle(pathname: string): string {
  if (pathname === "/app" || pathname === "/app/") return "Visão Executiva";
  if (pathname.includes("/financeiro/fluxo-matriz"))           return "Matriz de Fluxo por Filial";
  if (pathname.includes("/financeiro/fluxo-caixa"))           return "Fluxo de Caixa";
  if (pathname.includes("/financeiro/contas-a-receber"))       return "Contas a Receber";
  if (pathname.includes("/financeiro/contas-a-pagar"))         return "Contas a Pagar";
  if (pathname.includes("/financeiro/recebimentos"))           return "Recebimentos";
  if (pathname.includes("/financeiro/importacao-pagamentos"))  return "Import. Pagamentos";
  if (pathname.includes("/financeiro/compras"))                return "Compras";
  if (pathname.includes("/financeiro/estoque"))                return "Estoque";
  if (pathname.includes("/financeiro/custos"))                 return "Custos";
  if (pathname.includes("/financeiro/rateios"))                return "Rateios";
  if (pathname.includes("/financeiro/precificacao"))           return "Precificação";
  if (pathname.includes("/comercial/vendas"))                  return "Vendas";
  if (pathname.includes("/comercial/procedimentos"))           return "Procedimentos";
  if (pathname.includes("/assistencial"))                      return "Explorador Assistencial";
  if (pathname.includes("/estrutura"))                         return "Estrutura";
  if (pathname.includes("/integracoes"))                       return "Integrações";
  if (pathname.includes("/usuarios"))                          return "Usuários";
  if (pathname.includes("/configuracoes"))                     return "Configurações";
  return "Órbita";
}

// ---------------------------------------------------------------------------
// Avatar dropdown
// ---------------------------------------------------------------------------
function AvatarMenu() {
  const { signOut } = useAuth();
  const orbUser = useOrbUser();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const initial = (orbUser?.displayName ?? orbUser?.email ?? "?")[0]?.toUpperCase() ?? "?";

  // Fecha ao clicar fora
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => { document.removeEventListener("mousedown", handleClick); };
  }, []);

  return (
    <div ref={menuRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); }}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Menu do usuário"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          background: "var(--color-accent)",
          color: "var(--color-nav-accent-fg)",
          fontFamily: '"Inter", sans-serif',
          fontSize: "0.8125rem",
          fontWeight: 700,
          border: "none",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        {initial}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 0.5rem)",
            minWidth: "200px",
            background: "var(--color-nav-surface)",
            border: "1px solid var(--color-nav-border)",
            borderRadius: "6px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
            zIndex: 100,
            padding: "0.5rem 0",
          }}
        >
          {/* Info do usuário */}
          <div
            style={{
              padding: "0.625rem 1rem",
              borderBottom: "1px solid var(--color-nav-border)",
            }}
          >
            <p
              style={{
                fontFamily: '"Inter", sans-serif',
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: "var(--color-nav-text)",
                margin: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {orbUser?.displayName ?? orbUser?.email ?? "Usuário"}
            </p>
            {orbUser?.displayName && (
              <p
                style={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: "0.625rem",
                  /* Metadados role/tenant sobre a superficie navy: bone-dim
                     (~6.9:1); o cinza bruma antigo ficava em ~2.8:1. */
                  color: "var(--color-nav-text-muted)",
                  margin: "0.125rem 0 0",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {orbUser.claims.role} · {orbUser.claims.tenantId}
              </p>
            )}
          </div>

          {/* Sair */}
          <button
            type="button"
            role="menuitem"
            onClick={() => { void signOut(); }}
            style={{
              width: "100%",
              padding: "0.5rem 1rem",
              background: "none",
              border: "none",
              fontFamily: '"Inter", sans-serif',
              fontSize: "0.8125rem",
              color: "var(--color-nav-text-muted)",
              textAlign: "left",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--color-nav-surface-hover)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "none"; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
            Sair
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Topbar principal
// ---------------------------------------------------------------------------

interface AppTopbarProps {
  onMenuClick: () => void;
}

export function AppTopbar({ onMenuClick }: AppTopbarProps) {
  const location = useLocation();
  const { branding } = useTenant();
  const moduleTitle = resolveModuleTitle(location.pathname);

  return (
    <header
      style={{
        height: "56px",
        background: "var(--color-nav-bg)",
        borderBottom: "1px solid var(--color-nav-border)",
        display: "flex",
        alignItems: "center",
        padding: "0 1rem",
        gap: "0.75rem",
        position: "sticky",
        top: 0,
        zIndex: 50,
        flexShrink: 0,
      }}
    >
      {/* Botão hamburguer (mobile ≤480px) */}
      <button
        type="button"
        onClick={onMenuClick}
        aria-label="Abrir menu de navegação"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "36px",
          height: "36px",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--color-nav-text-muted)",
          borderRadius: "4px",
          flexShrink: 0,
        }}
        className="lg:hidden"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      {/* Logo do tenant + wordmark */}
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.375rem", flexShrink: 0 }}>
        {branding.logoUrl ? (
          <img
            src={branding.logoUrl}
            alt={`${branding.displayName} - Órbita`}
            style={{ height: "28px", objectFit: "contain" }}
          />
        ) : (
          <span
            style={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: "1rem",
              fontWeight: 500,
              color: "var(--color-nav-text)",
            }}
          >
            Órbita
          </span>
        )}
        {branding.mode !== "whiteLabel" && (
          <span
            style={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: "0.625rem",
              /* Assinatura discreta mas legivel sobre navy: token de apoio
                 mono (~6:1); o bruma escuro antigo ficava em ~2.1:1. */
              color: "var(--color-nav-group-label)",
              letterSpacing: "0.04em",
            }}
          >
            {/* Assinatura do PRODUTO: sempre "by Nectaris" (a holding dona do
                Órbita), nunca o nome do tenant/cliente. O white-label do tenant
                segue legítimo no logo, no tema (data-theme) e no nome da empresa
                exibido no dashboard (VisaoExecutivaPage); só a assinatura do
                produto é fixa. No modo whiteLabel puro a assinatura é omitida. */}
            by Nectaris
          </span>
        )}
      </div>

      {/* Divisor */}
      <div
        aria-hidden="true"
        style={{ width: "1px", height: "20px", background: "var(--color-nav-border)", flexShrink: 0 }}
      />

      {/* Título do módulo ativo */}
      <h2
        style={{
          fontFamily: '"Space Grotesk", sans-serif',
          fontSize: "0.9375rem",
          fontWeight: 600,
          color: "var(--color-nav-text)",
          margin: 0,
          flex: 1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {moduleTitle}
      </h2>

      {/* Avatar */}
      <AvatarMenu />
    </header>
  );
}
